import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as requests from "../api/requests";
import {
  addNotification,
  addErrorNotification,
  addWarningNotification,
} from "./notificationsSlice";

const initialState = {
  certsRemaining: NaN,
  eventsInfo: {
    events: [],
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
    },
  },
  blockchainKillSwitch: null,
  certData: {},
  imgHeight: 0,
  imgWidth: 0,
  imgSrc: "",
  certPreview: "",
  emailTemplatePreview: "",
  accountInfo: null,
  sectorTemplates: {},
  sectorTemplateStatus: {},
};

// ---------------------- ASYNC THUNKS ----------------------

export const getCertificatesRemaining = createAsyncThunk(
  "certificateIssuerHome/getCertificatesRemaining",
  async (_, thunkAPI) => {
    const res = await requests.getCertsRemaining();
    const body = await res.json();

    if (!res.ok) {
      thunkAPI.dispatch(
        addErrorNotification(
          body.errmsg || "Something Went Wrong When Fetching The Certificate Count!",
        ),
      );
    }

    return { ok: res.ok, body };
  },
);

export const getEventsInfo = createAsyncThunk(
  "certificateIssuerHome/getEventsInfo",
  async ({ page, pageSize } = {}, thunkAPI) => {
    const res = await requests.getCertificateIssuerHomeInfo(page, pageSize);
    const body = await res.json();

    if (!res.ok) {
      thunkAPI.dispatch(
        addErrorNotification(body.errmsg || "Something Went Wrong When Fetching Events Info!"),
      );
    }

    return { ok: res.ok, body };
  },
);

export const deleteEvent = createAsyncThunk(
  "certificateIssuerHome/deleteEvent",
  async (eventId, thunkAPI) => {
    const res = await requests.deleteEvent(eventId);
    const body = await res.json();

    if (res.ok) {
      thunkAPI.dispatch(getEventsInfo());
      thunkAPI.dispatch(addNotification("Event Has Been Deleted!"));
    } else {
      thunkAPI.dispatch(
        addErrorNotification(body.errmsg || "Something Went Wrong When Deleting The Event!"),
      );
    }

    return { ok: res.ok, body };
  },
);

export const uploadCertData = createAsyncThunk(
  "certificateIssuerHome/uploadCertData",
  async ({ columns, rows, eventId }, thunkAPI) => {
    const res = await requests.uploadCertData(eventId, { columns, rows });
    const body = await res.json();

    if (res.ok) {
      thunkAPI.dispatch(getEventsInfo());
      thunkAPI.dispatch(addNotification("File has been uploaded successfully!"));
    } else {
      thunkAPI.dispatch(
        addErrorNotification(body.errmsg || "Something Went Wrong When Uploading The Data!"),
      );
    }

    return { ok: res.ok, body };
  },
);

export const confirmCsvUpload = createAsyncThunk(
  "certificateIssuerHome/confirmCsvUpload",
  async (eventId, thunkAPI) => {
    const res = await requests.confirmCsvUpload(eventId);
    const body = await res.json();

    if (res.ok) {
      thunkAPI.dispatch(getEventsInfo());
      thunkAPI.dispatch(
        addNotification(
          `CSV confirmed! ${body.totalCertificates} certificates ready for issuance.`,
        ),
      );
    } else {
      thunkAPI.dispatch(
        addErrorNotification(body.errmsg || "Something Went Wrong When Confirming The CSV!"),
      );
    }

    return { ok: res.ok, body };
  },
);

export const uploadCertImage = createAsyncThunk(
  "certificateIssuerHome/uploadCertImage",
  async ({ imageFile, eventId }, thunkAPI) => {
    if (imageFile.size > 1_000_000) {
      thunkAPI.dispatch(addErrorNotification("Image should at most be 1mb in size"));
      return { ok: false };
    }

    const res = await requests.uploadCertImage(imageFile, eventId);
    const body = await res.json();

    if (res.ok) {
      thunkAPI.dispatch(getEventsInfo());
      thunkAPI.dispatch(addNotification("Image has been uploaded successfully!"));
    } else {
      thunkAPI.dispatch(
        addErrorNotification(body.errmsg || "Something Went Wrong When Uploading The Image!"),
      );
    }

    return { ok: res.ok, body };
  },
);

export const addNewProduction = createAsyncThunk(
  "certificateIssuerHome/addNewProduction",
  async ({ productionName, year, fieldConfigs }, thunkAPI) => {
    if (!productionName || !year) {
      thunkAPI.dispatch(addErrorNotification("Production Name and Year are required"));
      return { ok: false };
    }

    const res = await requests.addNewProduction(productionName, year, fieldConfigs);
    const body = await res.json();

    if (res.ok) {
      thunkAPI.dispatch(getEventsInfo());
      thunkAPI.dispatch(addNotification("Production Has Been Created!"));
    } else {
      thunkAPI.dispatch(
        addErrorNotification(body.errmsg || "Something Went Wrong When Creating The Production!"),
      );
    }

    return { ok: res.ok, body };
  },
);

export const updateThreshold = createAsyncThunk(
  "certificateIssuerHome/updateThreshold",
  async ({ updateThresholdEventId, currentThreshold }, thunkAPI) => {
    const res = await requests.updateThreshold(updateThresholdEventId, currentThreshold);

    if (res.ok) {
      thunkAPI.dispatch(getEventsInfo());
      thunkAPI.dispatch(addNotification("Threshold Has Been Updated!"));
    } else {
      thunkAPI.dispatch(
        addErrorNotification("Something Went Wrong When Attempting to Update Threshold"),
      );
    }

    return { ok: res.ok };
  },
);

export const issueCertificates = createAsyncThunk(
  "certificateIssuerHome/issueCertificates",
  async (eventId, thunkAPI) => {
    const res = await requests.issueCertificates(eventId);
    const body = await res.json();

    if (res.ok) {
      thunkAPI.dispatch(getEventsInfo());
      thunkAPI.dispatch(getCertificatesRemaining());

      if (body.warning) {
        thunkAPI.dispatch(addWarningNotification(body.warning));
      } else {
        thunkAPI.dispatch(
          addNotification(`${body.totalCertificates || 0} certificates issued successfully!`),
        );
      }
    } else {
      thunkAPI.dispatch(
        addErrorNotification(body.errmsg || "Something Went Wrong When Issuing Certificates!"),
      );
    }

    return { ok: res.ok, body };
  },
);

export const downloadQrs = createAsyncThunk(
  "certificateIssuerHome/downloadQrs",
  async (eventId, thunkAPI) => {
    const res = await requests.downloadQrs(eventId);
    if (!res.ok) {
      return;
    }

    const blob = await res.blob();

    // Trigger download directly inside the thunk
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `qrs-${eventId}.zip`);
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  },
);

export const downloadScanStats = createAsyncThunk(
  "certificateIssuerHome/downloadScanStats",
  async (eventId, thunkAPI) => {
    const res = await requests.downloadScanStats(eventId);
    const body = await res.json();

    if (res.ok) {
      thunkAPI.dispatch(addNotification("Scan statistics downloaded successfully"));
    } else {
      thunkAPI.dispatch(
        addErrorNotification(
          body.errmsg || "Something Went Wrong When Downloading Scan Statistics!",
        ),
      );
    }

    return { ok: res.ok, body };
  },
);

export const getCertData = createAsyncThunk(
  "certificateIssuerHome/getCertData",
  async (eventId, thunkAPI) => {
    const res = await requests.getCertData(eventId);
    const body = await res.json();

    if (!res.ok) {
      thunkAPI.dispatch(
        addErrorNotification(
          body.errmsg || "Something Went Wrong When Fetching The Certificate Data!",
        ),
      );
    }

    return { ok: res.ok, body };
  },
);

export const getCertImage = createAsyncThunk(
  "certificateIssuerHome/getCertImage",
  async (eventId, thunkAPI) => {
    const res = await requests.getCertImage(eventId);
    const body = await res.json();

    if (!res.ok) {
      thunkAPI.dispatch(
        addErrorNotification(
          body.errmsg || "Something Went Wrong When Fetching The Certificate Image!",
        ),
      );
    }

    return { ok: res.ok, body };
  },
);

export const getCertPreview = createAsyncThunk(
  "certificateIssuerHome/getCertPreview",
  async (args, thunkAPI) => {
    const res = await requests.getCertPreview(
      args.eventId,
      args.email,
      args.columnMapping,
      args.qrCodeMapping,
      args.displayQrCode,
    );
    const body = await res.json();

    if (!res.ok) {
      thunkAPI.dispatch(
        addErrorNotification(
          body.errmsg || "Something Went Wrong When Fetching The Certificate Preview!",
        ),
      );
    }

    return { ok: res.ok, body };
  },
);

export const getEmailTemplatePreview = createAsyncThunk(
  "certificateIssuerHome/getEmailTemplatePreview",
  async ({ eventId, email, template }, thunkAPI) => {
    const res = await requests.getEmailTemplatePreview(eventId, email, template);
    const body = await res.json();

    if (!res.ok) {
      thunkAPI.dispatch(
        addErrorNotification(
          body.errmsg || "Something Went Wrong When Fetching The Email Template Preview!",
        ),
      );
    }

    return { ok: res.ok, body };
  },
);

export const fetchSectorTemplate = createAsyncThunk(
  "certificateIssuerHome/fetchSectorTemplate",
  async (sectorCode, thunkAPI) => {
    const state = thunkAPI.getState();
    const cached = state?.certificateIssuerHome?.sectorTemplates?.[sectorCode];
    if (cached) {
      return { sectorCode, template: cached };
    }

    const res = await requests.getSectorTemplate(sectorCode);
    const body = await res.json();

    if (!res.ok) {
      thunkAPI.dispatch(addErrorNotification(body.errmsg || "Unable to fetch sector template!"));
      throw new Error();
    }

    return { sectorCode, template: body.template };
  },
);

// ---------------------- SLICE ----------------------

export const certificateIssuerHomeSlice = createSlice({
  name: "certificateIssuerHome",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCertificatesRemaining.fulfilled, (state, { payload }) => {
        if (payload.ok) {
          state.certsRemaining = payload.body.certsRemaining;
          state.accountInfo = {
            sector: payload.body.sector,
            sectorName: payload.body.sectorName,
          };
        }
      })
      .addCase(getEventsInfo.fulfilled, (state, { payload }) => {
        if (payload.ok) {
          state.eventsInfo = {
            events: payload.body.events,
            pagination: payload.body.pagination,
          };
          state.blockchainKillSwitch = payload.body.blockchainKillSwitch || null;
        }
      })
      .addCase(getCertData.fulfilled, (state, { payload }) => {
        if (payload.ok) {
          state.certData = payload.body.certData;
        }
      })
      .addCase(getCertImage.fulfilled, (state, { payload }) => {
        if (payload.ok) {
          const { image, height, width } = payload.body;
          const blob = new Blob([Buffer.from(image, "base64")]);
          state.imgSrc = URL.createObjectURL(blob);
          state.imgHeight = height;
          state.imgWidth = width;
        }
      })
      .addCase(getCertPreview.fulfilled, (state, { payload }) => {
        if (payload.ok) {
          state.certPreview = payload.body.certPreview;
        }
      })
      .addCase(getEmailTemplatePreview.fulfilled, (state, { payload }) => {
        if (payload.ok) {
          state.emailTemplatePreview = payload.body.emailTemplatePreview;
        }
      })
      .addCase(fetchSectorTemplate.pending, (state, action) => {
        state.sectorTemplateStatus[action.meta.arg] = "loading";
      })
      .addCase(fetchSectorTemplate.fulfilled, (state, { payload }) => {
        state.sectorTemplates[payload.sectorCode] = payload.template;
        state.sectorTemplateStatus[payload.sectorCode] = "succeeded";
      })
      .addCase(fetchSectorTemplate.rejected, (state, action) => {
        state.sectorTemplateStatus[action.meta.arg] = "failed";
      });
  },
});

export default certificateIssuerHomeSlice.reducer;
